import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as DeviceStatusIcon } from '../../../../assets/icons/dot-circle.svg';
import { PersonalChargerInterFace } from '../../../../interface/end-consumer-personal-charger-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchPersonalCharger,
  setSortData,
} from '../../../../redux/slices/EndConsumerPersonalChargerSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import DeviceSidePanel from '../../../../shared-components/table-side-panel/DeviceSidePanel';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';

/**
 * personal Charger
 * @returns
 */
function PersonalChargers() {
  const { t } = useTranslation('endConsumer');
  const { personalCharger, limit, page, loading, totalPages, totalResults, sortBy, sortField } =
    useAppSelector((state) => state.ConsumerPersonalCharger);
  const dispatch = useAppDispatch();
  const [selectedStation, setSelectedStation] = useState<PersonalChargerInterFace | null>(null);
  const [openStatus, setOpenStatus] = useState(false);
  const { selectedEndConsumer, endConsumerType } = useAppSelector((state) => state.endConsumerType);

  /**
   * Fetch Devices list
   */
  const fetchData = (data: any) => {
    if (!loading && selectedEndConsumer?.userCustomId) {
      dispatch(fetchPersonalCharger({ userCustomId: selectedEndConsumer.userCustomId, ...data }));
    }
  };

  /**
   * UI of Status
   * @param device
   * @returns
   */
  const renderStatus = (device: PersonalChargerInterFace) =>
    device?.isDeviceOffline !== undefined ? (
      <div>
        <div className={device.isDeviceOffline ? 'tag-danger-label' : 'tag-success-label'}>
          <DeviceStatusIcon />
          <span style={{ fontWeight: '700' }}>
            {device.isDeviceOffline ? t('offline') : t('online')}
          </span>
        </div>
      </div>
    ) : (
      <div />
    );

  /**
   * handleDevices
   */
  const handleDevices = (device: any) => {
    setSelectedStation(device);
    setOpenStatus(true);
  };

  const column = [
    {
      label: t('deviceName'),
      accessorKey: 'chargingPointName' as keyof PersonalChargerInterFace,
      sortAllow: true,
    },
    {
      label: t('device'),
      accessorKey: 'chargingPointSn' as keyof PersonalChargerInterFace,
      sortAllow: true,
      /**
       *  Side panel component to show on clicking the cell value
       */
      customData: (device: PersonalChargerInterFace) => (
        <p
          role="presentation"
          className="accounts-management"
          onClick={() => {
            handleDevices(device);
          }}>
          {device.chargingPointSn}
        </p>
      ),
    },
    {
      label: t('status'),
      accessorKey: 'isDeviceOffline' as keyof PersonalChargerInterFace,
      customData: renderStatus,
      sortAllow: true,
    },
  ];

  /**
   * Table pagination function
   */
  const handlePageOrLimitChange = (value: any) => {
    if (value.sortField && value.sortBy) {
      dispatch(setSortData({ sortField: value.sortField, sortBy: value.sortBy as 'asc' | 'desc' }));
    }
    fetchData({ ...value });
  };

  // Fetch when accountCustomId, page, or limit changes
  useEffect(() => {
    if (personalCharger.length === 0) {
      fetchData({
        page,
        limit,
      });
    }
  }, [personalCharger, page, limit, selectedEndConsumer]);

  return (
    <div className="personal-charger-wrap">
      {selectedStation && (
        <SidePaneModel
          open={openStatus}
          content={<DeviceSidePanel fetchIdFrom="props" id={selectedStation._id} />}
          closePanel={() => {
            setOpenStatus(false);
          }}
          url={`/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/device/${selectedStation?.chargingPointSn}/device-details?searchBy=${selectedStation?.chargingPointName}`}
        />
      )}

      <CustomTable
        header={column}
        serialNo
        sortData={{ sortBy, sortField }}
        limit={limit}
        totalPages={totalPages}
        data={personalCharger}
        page={page}
        loading={loading}
        totalResults={totalResults}
        pageLimitChange={handlePageOrLimitChange}
        height={370}
      />
    </div>
  );
}

export default PersonalChargers;
